import { Fragment } from "react";
import { Text, chakra, type TextProps } from "@chakra-ui/react";
import {
  NOTIFICATION_CONSENT_DISCLOSURE,
  NOTIFICATION_CONSENT_POLICY_PHRASES,
  type NotificationConsentPolicyPhrase,
} from "@greenflagdate/shared";

// Where each linked phrase points. The phrase list is owned by shared (next to
// the disclosure text, so a copy change that drops one fails the constants
// test); this map just supplies the hrefs. Keyed by the phrase union, so adding
// a phrase in shared is a compile error here until it gets an href. Links open
// in a new tab so clicking one never loses the registration/settings flow.
const POLICY_HREFS: Record<NotificationConsentPolicyPhrase, string> = {
  "Terms of Service": "/policy/terms",
  "Privacy Policy": "/policy/privacy",
};

const splitPattern = new RegExp(
  `(${NOTIFICATION_CONSENT_POLICY_PHRASES.join("|")})`,
  "g"
);

// Renders the canonical consent disclosure with the policy phrases linked. The
// text is NOT modified — it's the exact string we hash and store on the consent
// record — so the linked words match the recorded words byte for byte; the
// anchors are display-only affordances. Styled as legal fine print; callers
// pass only layout (spacing).
export const NotificationConsentDisclosure = (props: TextProps) => {
  const parts = NOTIFICATION_CONSENT_DISCLOSURE.split(splitPattern);
  return (
    <Text textStyle="legalFine" color="fg.muted" {...props}>
      {parts.map((part, i) => {
        const href = (POLICY_HREFS as Record<string, string | undefined>)[part];
        if (!href) {
          return <Fragment key={i}>{part}</Fragment>;
        }
        return (
          <chakra.a
            key={i}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            color="fg.brand"
            textDecoration="underline"
            _hover={{ textDecoration: "none" }}
          >
            {part}
          </chakra.a>
        );
      })}
    </Text>
  );
};
